import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetDataGrid from "./components/KuyumcuDataGrid";
import ProsetPopup from "./components/ProsetPopup";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";

import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
} from "@mui/material";

import {
  FaUserShield,
} from "react-icons/fa";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Edit } from "@mui/icons-material";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuYonetimPanelPage.css";

const uyelikTipOptions = [
  { value: "", label: "Tümü" },
  { value: "BILINMEYEN", label: "BILINMEYEN" },
  { value: "SUPER_USER", label: "SUPER USER" },
  { value: "KUYUMCU", label: "KUYUMCU" },
  { value: "ORTAK", label: "ORTAK" },
  { value: "CANTACI", label: "CANTACI" },
  { value: "PERSONEL", label: "PERSONEL" },
];

const kuyumcuTipiOptions = [
  { value: "", label: "Tümü" },
  { value: "ATOLYE_KUYUMCU", label: "Atölye" },
  { value: "VITRIN_KUYUMCU", label: "Vitrin" },
];

const toYmd = (d) => {
  if (!d) return "";
  // d: yyyy-mm-dd beklenir (TextField type="date")
  return d;
};

export default function KuyumcuYonetimPanelPage() {
  const navigate = useNavigate();
  const userId = getUserId();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  // login guard (senin sayfalarda aynı pattern var)【turn10file12†KuyumcuParametrelerPage.jsx†L33-L38】
  useEffect(() => {
    if (!userId || !token) navigate("/login");
  }, [userId, token, navigate]);

  // ----------------------
  // STATE: liste + loading
  // ----------------------
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  // ----------------------
  // FILTERS
  // ----------------------
  const [f, setF] = useState({
    isim: "",
    soyisim: "",
    firmaAdi: "",
    uyelikTip: "",
    kuyumcuTipi: "",
    uyelikStart: "",
    uyelikEnd: "",
    lisansStart: "",
    lisansEnd: "",
    firmaIl: "",
    firmaIlce: "",
  });

  const resetFilters = () => {
    setF({
      isim: "",
      soyisim: "",
      firmaAdi: "",
      uyelikTip: "",
      kuyumcuTipi: "",
      uyelikStart: "",
      uyelikEnd: "",
      lisansStart: "",
      lisansEnd: "",
      firmaIl: "",
      firmaIlce: "",
    });
  };

  // ----------------------
  // LOAD USERS
  // ----------------------
  const loadAllUsers = async () => {
    setLoading(true);
    setError("");
    try {
      // Mevcut backend’inde /api/auth GET tüm kullanıcıları döndürüyor.
      // (İleride bunu /api/admin/users yapmanı öneririm.)
      const resp = await axiosInstance.get("/admin/users");
      setRows(resp.data || []);
    } catch (e) {
      console.error(e);
      setError("Kullanıcılar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAllUsers();
    // eslint-disable-next-line
  }, []);

  // ----------------------
  // FILTER APPLY (client-side)
  // ----------------------
  const filteredRows = useMemo(() => {
    const isim = (f.isim || "").trim().toLowerCase();
    const soyisim = (f.soyisim || "").trim().toLowerCase();
    const firmaAdi = (f.firmaAdi || "").trim().toLowerCase();
    const firmaIl = (f.firmaIl || "").trim().toLowerCase();
    const firmaIlce = (f.firmaIlce || "").trim().toLowerCase();

    const uyelikStart = toYmd(f.uyelikStart);
    const uyelikEnd = toYmd(f.uyelikEnd);
    const lisansStart = toYmd(f.lisansStart);
    const lisansEnd = toYmd(f.lisansEnd);

    const inDateRange = (value, start, end) => {
      if (!value) return true;
      const v = String(value); // LocalDate -> "yyyy-mm-dd" gelmesi muhtemel
      if (start && v < start) return false;
      if (end && v > end) return false;
      return true;
    };

    return (rows || []).filter((r) => {
      const rIsim = (r?.isim || "").toLowerCase();
      const rSoy = (r?.soyisim || "").toLowerCase();
      const rFirma = (r?.firmaAdi || "").toLowerCase();
      const rIl = (r?.firmaIl || "").toLowerCase();
      const rIlce = (r?.firmaIlce || "").toLowerCase();

      if (isim && !rIsim.includes(isim)) return false;
      if (soyisim && !rSoy.includes(soyisim)) return false;
      if (firmaAdi && !rFirma.includes(firmaAdi)) return false;
      if (firmaIl && !rIl.includes(firmaIl)) return false;
      if (firmaIlce && !rIlce.includes(firmaIlce)) return false;

      if (f.uyelikTip && r?.uyelikTip !== f.uyelikTip) return false;
      if (f.kuyumcuTipi && r?.kuyumcuTipi !== f.kuyumcuTipi) return false;

      if (!inDateRange(r?.uyelikTarihi, uyelikStart, uyelikEnd)) return false;
      if (!inDateRange(r?.lisansGecerlilikTarihi, lisansStart, lisansEnd)) return false;

      return true;
    });
  }, [rows, f]);

  // ----------------------
  // POPUP (EDIT)
  // ----------------------
  const [popupOpen, setPopupOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [form, setForm] = useState({
    id: null,
    firmaId: null,
    ustKullaniciId: null,
    uyelikTarihi: "",
    // editable:
    kullaniciAdi: "",
    yeniSifre: "",
    lisansGecerlilikTarihi: "",
    aktifUyeMi: true,
    isim: "",
    soyisim: "",
    telNo: "",
    mail: "",
    uyelikTip: "",
    kuyumcuTipi: "",
    firmaAdi: "",
    firmaIl: "",
    firmaIlce: "",
    firmaAdres: "",
    kullaniciNotu: "",
  });

  const openEdit = (row) => {
    setEditUser(row);

    setForm({
      id: row?.id ?? null,
      firmaId: row?.firmaId ?? null,
      ustKullaniciId: row?.ustKullaniciId ?? null,
      uyelikTarihi: row?.uyelikTarihi ?? "",
      kullaniciAdi: row?.kullaniciAdi ?? "",
      yeniSifre: "",
      lisansGecerlilikTarihi: row?.lisansGecerlilikTarihi ?? "",
      aktifUyeMi: !!row?.aktifUyeMi,
      isim: row?.isim ?? "",
      soyisim: row?.soyisim ?? "",
      telNo: row?.telNo ?? "",
      mail: row?.mail ?? "",
      uyelikTip: row?.uyelikTip ?? "",
      kuyumcuTipi: row?.kuyumcuTipi ?? "",
      firmaAdi: row?.firmaAdi ?? "",
      firmaIl: row?.firmaIl ?? "",
      firmaIlce: row?.firmaIlce ?? "",
      firmaAdres: row?.firmaAdres ?? "",
      kullaniciNotu: row?.kullaniciNotu ?? "",
    });

    setPopupOpen(true);
  };

  const closePopup = () => {
    setPopupOpen(false);
    setEditUser(null);
    setForm((p) => ({ ...p, yeniSifre: "" }));
  };

  // ----------------------
  // SAVE (requires backend admin endpoints)
  // ----------------------
  const saveUser = async () => {
    if (!form.id) return;

    setLoading(true);
    setError("");

    try {
      // 1) kullanıcı güncelleme (editable alanlar)
      // ÖNERİLEN endpoint: PATCH /api/admin/users/{id}
      // Backend’de yoksa: bunu eklemen gerekecek.
      const payload = {
        kullaniciAdi: form.kullaniciAdi,
        lisansGecerlilikTarihi: form.lisansGecerlilikTarihi || null,
        aktifUyeMi: !!form.aktifUyeMi,
        isim: form.isim,
        soyisim: form.soyisim,
        telNo: form.telNo,
        mail: form.mail,
        uyelikTip: form.uyelikTip,
        kuyumcuTipi: form.kuyumcuTipi,
        firmaAdi: form.firmaAdi,
        firmaIl: form.firmaIl,
        firmaIlce: form.firmaIlce,
        firmaAdres: form.firmaAdres,
        kullaniciNotu: form.kullaniciNotu,
      };

      await axiosInstance.patch(`/admin/users/${form.id}`, payload);

      // 2) şifre reset (yeni şifre girilmişse)
      // ÖNERİLEN endpoint: PATCH /api/admin/users/{id}/password
      if (form.yeniSifre && form.yeniSifre.trim().length > 0) {
        await axiosInstance.patch(`/admin/users/${form.id}/password`, {
          newPassword: form.yeniSifre,
          newPasswordConfirm: form.yeniSifre,
        });
      }

      await loadAllUsers();
      closePopup();
      alert("Kullanıcı güncellendi.");
    } catch (e) {
      console.error(e);
      setError("Güncelleme başarısız. (Admin şifrenizi kurallara uygun koyun (Şifre en az 8 karakter olmalı ve harf + sayı içermelidir!).)");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------
  // COLUMNS
  // ----------------------
  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "firmaId", headerName: "Firma ID", width: 100 },
      { field: "firmaAdi", headerName: "Firma Adı", width: 160 },
      { field: "isim", headerName: "İsim", width: 120 },
      { field: "soyisim", headerName: "Soyisim", width: 120 },
      { field: "uyelikTip", headerName: "Üyelik Tipi", width: 120 },
      {
          field: "kuyumcuTipi",
          headerName: "Kuyumcu Tipi",
          width: 150,
          renderCell: (params) => {
            const v = params?.row?.kuyumcuTipi;

            if (v === "ATOLYE_KUYUMCU") return "ATÖLYE";
            if (v === "VITRIN_KUYUMCU") return "VİTRİN";

            return "-";
          },
      },
      { field: "uyelikTarihi", headerName: "Üyelik Tarihi", width: 130 },
      { field: "lisansGecerlilikTarihi", headerName: "Lisans Geçerlilik", width: 150 },

      { field: "firmaIl", headerName: "İl", width: 120 },
      { field: "firmaIlce", headerName: "İlçe", width: 120 },
      { field: "firmaAdres", headerName: "Adres", width: 220 },

//       { field: "ustKullaniciId", headerName: "Üst Kullanıcı ID", width: 120 },

      { field: "kullaniciAdi", headerName: "Username", width: 140 },

      // Şifreyi hash’ten düz metin çıkaramayız: maskeliyoruz
      {
        field: "password",
        headerName: "Password",
        width: 130,
        valueGetter: () => "••••••••",
      },
      { field: "telNo", headerName: "Telefon", width: 130 },
      { field: "mail", headerName: "Mail", width: 200 },

      {
        field: "aktifUyeMi",
        headerName: "Aktif mi",
        width: 110,
        valueGetter: (params) => (params?.row?.aktifUyeMi ? "Hayır" : "Evet"),
      },

      { field: "kullaniciNotu", headerName: "Kullanıcı Notu", width: 220 },

      {
        field: "_actions",
        headerName: "İşlemler",
        width: 110,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            onClick={() => openEdit(params.row)}
            sx={{
              background: "#b8860b",
              color: "white",
              "&:hover": { background: "#d4a017" },
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
        ),
      },
    ],
    // eslint-disable-next-line
    []
  );

  return (
    <div className="kuyumcu-page-container">
      <PremiumBackground />
      <KuyumcuLeftSideNavbar />
      <div className="kuyumcu-page-content">
        <Box className="yp-header">
          <Typography variant="h5" className="page-title">
            <FaUserShield /> Yönetim Paneli
          </Typography>
          <Box className="yp-header-actions">
            <Button className="yp-reload-btn" onClick={loadAllUsers} disabled={loading}>
              Yenile
            </Button>
          </Box>
        </Box>

        {/* FILTER BAR */}
        <Box className="yp-filter-card">
          <Typography className="yp-filter-title">Kullanıcı Arama / Filtre</Typography>

          <Box className="yp-filter-grid">
            <TextField
              label="İsim"
              value={f.isim}
              onChange={(e) => setF((p) => ({ ...p, isim: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Soyisim"
              value={f.soyisim}
              onChange={(e) => setF((p) => ({ ...p, soyisim: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Firma Adı"
              value={f.firmaAdi}
              onChange={(e) => setF((p) => ({ ...p, firmaAdi: e.target.value }))}
              fullWidth
            />
            <TextField
              select
              label="Üyelik Tipi"
              value={f.uyelikTip}
              onChange={(e) => setF((p) => ({ ...p, uyelikTip: e.target.value }))}
              fullWidth
            >
              {uyelikTipOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Kuyumcu Tipi"
              value={f.kuyumcuTipi}
              onChange={(e) => setF((p) => ({ ...p, kuyumcuTipi: e.target.value }))}
              fullWidth
            >
              {kuyumcuTipiOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              label="Üyelik Tarihi (Başlangıç)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={f.uyelikStart}
              onChange={(e) => setF((p) => ({ ...p, uyelikStart: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Üyelik Tarihi (Bitiş)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={f.uyelikEnd}
              onChange={(e) => setF((p) => ({ ...p, uyelikEnd: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Lisans Geçerlilik (Başlangıç)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={f.lisansStart}
              onChange={(e) => setF((p) => ({ ...p, lisansStart: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Lisans Geçerlilik (Bitiş)"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={f.lisansEnd}
              onChange={(e) => setF((p) => ({ ...p, lisansEnd: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Firma İl"
              value={f.firmaIl}
              onChange={(e) => setF((p) => ({ ...p, firmaIl: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Firma İlçe"
              value={f.firmaIlce}
              onChange={(e) => setF((p) => ({ ...p, firmaIlce: e.target.value }))}
              fullWidth
            />
          </Box>

          <Box className="yp-filter-actions">
            <Button className="yp-clear-btn" onClick={resetFilters} disabled={loading}>
              Temizle
            </Button>
          </Box>
        </Box>

        {error && (
          <Box mb={2}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {/* ACCORDION: Kullanıcı Yönetimi */}
        <Accordion defaultExpanded className="yp-accordion">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="yp-accordion-title">Kullanıcı Yönetimi</Typography>
          </AccordionSummary>

          <AccordionDetails>
            <Box className="yp-grid-shell">
              {loading ? (
                <Box className="yp-loading">
                  <CircularProgress />
                </Box>
              ) : (
                <ProsetDataGrid rows={filteredRows} columns={columns} />
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* EDIT POPUP */}
        <ProsetPopup
          open={popupOpen}
          title="Kullanıcı Düzenle"
          editMode={false}
          sabitMi={true}
          onCancel={closePopup}
          onSave={saveUser}
          extraActions={
            <Button
              onClick={() => {
                if (editUser?.id) openEdit(editUser);
              }}
              sx={{
                background: "#1e88e5",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { background: "#1565c0" },
              }}
            >
              Formu Yenile
            </Button>
          }
        >
          <div className="yp-edit-grid">
            {/* DISABLED */}
            <TextField label="ID" value={form.id ?? ""} disabled fullWidth />
            <TextField label="Firma ID" value={form.firmaId ?? ""} disabled fullWidth />
            <TextField label="Üst Kullanıcı ID" value={form.ustKullaniciId ?? ""} disabled fullWidth />
            <TextField label="Üyelik Tarihi" value={form.uyelikTarihi ?? ""} disabled fullWidth />

            {/* EDITABLE */}
            <TextField
              label="Username"
              value={form.kullaniciAdi}
              onChange={(e) => setForm((p) => ({ ...p, kullaniciAdi: e.target.value }))}
              fullWidth
            />

            <TextField
              label="Yeni Şifre (opsiyonel)"
              type="password"
              value={form.yeniSifre}
              onChange={(e) => setForm((p) => ({ ...p, yeniSifre: e.target.value }))}
              fullWidth
              helperText="Şifre hash’ten geri çözülemez; yalnızca yeni şifre set edilebilir."
            />

            <TextField
              label="Lisans Geçerlilik Tarihi"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.lisansGecerlilikTarihi || ""}
              onChange={(e) => setForm((p) => ({ ...p, lisansGecerlilikTarihi: e.target.value }))}
              fullWidth
            />

            <FormControlLabel
              control={
                <Switch
                  checked={!!form.aktifUyeMi}
                  onChange={(e) => setForm((p) => ({ ...p, aktifUyeMi: e.target.checked }))}
                />
              }
              label={form.aktifUyeMi ? "Aktif" : "Pasif"}
            />

            <TextField
              label="İsim"
              value={form.isim}
              onChange={(e) => setForm((p) => ({ ...p, isim: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Soyisim"
              value={form.soyisim}
              onChange={(e) => setForm((p) => ({ ...p, soyisim: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Telefon"
              value={form.telNo}
              onChange={(e) => setForm((p) => ({ ...p, telNo: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Mail"
              value={form.mail}
              onChange={(e) => setForm((p) => ({ ...p, mail: e.target.value }))}
              fullWidth
            />

            <TextField
              select
              label="Üyelik Tipi"
              value={form.uyelikTip}
              onChange={(e) => setForm((p) => ({ ...p, uyelikTip: e.target.value }))}
              fullWidth
            >
              {uyelikTipOptions
                .filter((x) => x.value !== "") // popup’ta “tümü” olmasın
                .map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              select
              label="Kuyumcu Tipi"
              value={form.kuyumcuTipi}
              onChange={(e) => setForm((p) => ({ ...p, kuyumcuTipi: e.target.value }))}
              fullWidth
            >
              {kuyumcuTipiOptions
                .filter((x) => x.value !== "")
                .map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
            </TextField>

            <TextField
              label="Firma Adı"
              value={form.firmaAdi}
              onChange={(e) => setForm((p) => ({ ...p, firmaAdi: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Firma İl"
              value={form.firmaIl}
              onChange={(e) => setForm((p) => ({ ...p, firmaIl: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Firma İlçe"
              value={form.firmaIlce}
              onChange={(e) => setForm((p) => ({ ...p, firmaIlce: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Firma Adres"
              value={form.firmaAdres}
              onChange={(e) => setForm((p) => ({ ...p, firmaAdres: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              sx={{
                "& .MuiInputBase-root": { height: "auto !important" },
              }}
            />
            <TextField
              label="Kullanıcı Notu"
              value={form.kullaniciNotu}
              onChange={(e) => setForm((p) => ({ ...p, kullaniciNotu: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
              sx={{
                "& .MuiInputBase-root": { height: "auto !important" },
              }}
            />
          </div>
        </ProsetPopup>
      </div>
    </div>
  );
}
